/**
 * @file SeoChecklist.tsx
 * @description Помогает редактору контролировать SEO-meta (phase12.0).
 *
 * Показывает:
 *   - Длину title (рекомендация 50-60).
 *   - Длину description (150-160).
 *   - Чек-лист: cover alt, ≥1 H2, ≥300 слов, нет noIndex.
 */

import { Check, X } from "lucide-react";

interface SeoChecklistProps {
  title: string;
  description: string;
  coverAlt: string;
  bodyHtml: string;
  noIndex: boolean;
}

interface CheckItem {
  ok: boolean;
  label: string;
}

export function SeoChecklist({
  title,
  description,
  coverAlt,
  bodyHtml,
  noIndex,
}: SeoChecklistProps) {
  const titleLen = title.length;
  const descLen = description.length;
  const wordCount = bodyHtml
    .replace(/<[^>]*>/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  const h2Count = (bodyHtml.match(/<h2\b/gi) ?? []).length;

  const items: CheckItem[] = [
    { ok: coverAlt.trim().length > 0, label: "Заполнен alt у обложки" },
    { ok: h2Count >= 1, label: "Есть минимум 1 заголовок H2" },
    { ok: wordCount >= 300, label: `Минимум 300 слов (сейчас ${wordCount})` },
    { ok: !noIndex, label: "Индексация разрешена (без noIndex)" },
  ];

  return (
    <div className="flex flex-col gap-3 rounded-md border border-on-surface/20 bg-surface-2 p-4">
      <h3 className="text-sm font-semibold">SEO-проверка</h3>

      <Meter
        label="Длина title"
        value={titleLen}
        min={50}
        max={60}
        message={
          titleLen === 0
            ? "Заполните title"
            : titleLen < 50
              ? "Коротко — Google дополнит"
              : titleLen > 60
                ? "Длинно — Google обрежет"
                : "Оптимально"
        }
      />
      <Meter
        label="Длина description"
        value={descLen}
        min={150}
        max={160}
        message={
          descLen === 0
            ? "Заполните description"
            : descLen < 150
              ? "Коротко"
              : descLen > 160
                ? "Длинно — Google обрежет"
                : "Оптимально"
        }
      />

      <ul className="flex flex-col gap-1 text-sm">
        {items.map((it) => (
          <li key={it.label} className="flex items-center gap-2">
            {it.ok ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <X className="h-4 w-4 text-rose-600" />
            )}
            <span className={it.ok ? "text-on-surface" : "text-on-surface-muted"}>
              {it.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Meter({
  label,
  value,
  min,
  max,
  message,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  message: string;
}) {
  const ok = value >= min && value <= max;
  const status = value === 0 ? "empty" : ok ? "ok" : value < min ? "short" : "long";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span
          className={
            status === "ok"
              ? "text-emerald-600"
              : status === "empty"
                ? "text-on-surface-muted"
                : "text-amber-600"
          }
        >
          {value} / {max}
        </span>
      </div>
      <div className="text-xs text-on-surface-muted">{message}</div>
    </div>
  );
}
